import * as types from 'constants/ActionTypes';
import { DEALERS_URL, DEALERS_API_URL, DEALERS_FILTER_URL, DEALERS_NAME_CHECK_URL, DEALERS_REDUCE_ALL_URL, USERS_API_BULK_INSERT_SHEET_URL, KEYLOOP_API_URL } from 'constants/ApiConstants';
import { DEFAULT } from 'constants/ExtensionConstants';

import { resolveLink } from 'services/UrlResolver';

import * as actionTypes from './actionTypes';
import { DEFAULT_DEALERS_FIELDS } from './constants';
import DealerService from '../../services/api/DealerService';

/**
 * @typedef {import('modules/dealers/types').Dealer} Dealer
 */

const prepareParams = ({ fields = DEFAULT_DEALERS_FIELDS, ...params } = {}) => ({
  ...params,
  fields: [...new Set([...fields.split(','), 'status'])].join(','),
});

/**
 * @description This action is only directly use by the dealer dashboard
 * @param {string=} link
 * @param {Object<string, any>=} parameters
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealersAction}
 */
export const fetchDealers = (link, parameters, meta = {}) => {
  const { url, params } = resolveLink(link, DEALERS_FILTER_URL, prepareParams(parameters));

  return {
    type: types.FETCH_DEALERS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url,
        params: {
          sort: 'name,asc',
          projection: 'details',
          ...params,
        },
      },
    },
    meta,
  };
};

/**
 * @param {string} error
 * @returns {import('modules/dealers/types').FetchDealersFailureAction}
 */
export const fetchDealersFailure = error => ({
  type: types.FETCH_DEALERS_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {import('modules/global/generic.types.js').PageContent<Dealer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealersSuccessAction}
 */
export const fetchDealersSuccess = (data, meta = {}) => ({
  type: types.FETCH_DEALERS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} [link]
 * @param {Object} [parameters]
 * @param {import('modules/global/typing/enums/extensions.enum').ExtensionEnum} [extension]
 * @param {boolean} [autoSelect]
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealersSelectAction}
 */
export const fetchDealersSelect = (link, parameters, extension, autoSelect, meta = {}) => {
  const { url, params } = resolveLink(link, DEALERS_FILTER_URL, parameters);

  return {
    type: types.FETCH_DEALERS_SELECT_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url,
        params,
      },
    },
    meta: { ...meta, extension, autoSelect },
  };
};

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealersSelectFailureAction}
 */
export const fetchDealersSelectFailure = (message, meta = {}) => ({
  type: types.FETCH_DEALERS_SELECT_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {import('modules/global/generic.types.js').PageContent<Dealer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealersSelectSuccessAction}
 */
export const fetchDealersSelectSuccess = (data, meta = { extension: DEFAULT }) => ({
  type: types.FETCH_DEALERS_SELECT_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} dealerId
 * @param {Object} params
 * @param {import('modules/global/typing/enums/extensions.enum').ExtensionEnum | string} extension
 * @param {Object} meta
 * @returns {import('modules/dealers/types').SelectDealerAction}
 */
export const selectDealer = (dealerId, params = undefined, extension = DEFAULT, meta = {}) => ({
  type: types.SELECT_DEALER,
  payload: {
    dealerId,
    params,
  },
  meta: {
    extension,
    ...meta,
  },
});

/**
 * @param {string} dealerId
 * @param {Object} [params={}]
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealerAction}
 */
export const fetchDealer = (dealerId, params = {}, meta = {}) => ({
  type: types.FETCH_DEALER_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${DEALERS_API_URL}/${dealerId}`,
      params,
    },
    dealerId,
  },
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealerFailureAction}
 */
export const fetchDealerFailure = (error, meta = {}) => ({
  type: types.FETCH_DEALER_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {Dealer} dealer
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FetchDealerSuccessAction}
 */
export const fetchDealerSuccess = (dealer, meta = {}) => ({
  type: types.FETCH_DEALER_SUCCESS,
  payload: dealer,
  meta,
});

/**
 * @param {Dealer} dealer
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').CreateDealerAction}
 */
export const createDealerRequest = (dealer, meta = {}) => ({
  type: types.CREATE_DEALER_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: DEALERS_API_URL,
      data: dealer,
    },
  },
  meta,
});

/**
 * @param {Dealer} dealer
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').CreateDealerSuccessAction}
 */
export const createDealerSuccess = (dealer, meta = {}) => ({
  type: types.CREATE_DEALER_SUCCESS,
  payload: dealer,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').CreateDealerFailureAction}
 */
export const createDealerFailure = (error, meta = {}) => ({
  type: types.CREATE_DEALER_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} dealerId
 * @param {Dealer} data
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').UpdateDealerAction}
 */
export const updateDealerRequest = (dealerId, data, meta = {}) => ({
  type: types.UPDATE_DEALER_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${DEALERS_API_URL}/${dealerId}`,
      data,
    },
  },
  meta,
});

/**
 * @param {Dealer} dealer
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').UpdateDealerSuccessAction}
 */
export const updateDealerSuccess = (dealer, meta = {}) => ({
  type: types.UPDATE_DEALER_SUCCESS,
  payload: dealer,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').UpdateDealerFailureAction}
 */
export const updateDealerFailure = (error, meta = {}) => ({
  type: types.UPDATE_DEALER_FAILURE,
  payload: error,
  error: true,
  meta
});

/**
 * @param {string} dealerId
 * @param {import('modules/global/typing/enums/dealers.enum').DealerStatusEnum} status
 * @returns {import('modules/dealers/types').ChangeDealerStatusAction}
 */
export const changeDealerStatusRequest = (dealerId, status, meta = {}) => ({
  type: types.CHANGE_DEALER_STATUS_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${DEALERS_API_URL}/${dealerId}/status`,
      data: {
        status,
      },
    },
  },
  meta,
});

/**
 * @param {Dealer} dealer
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').ChangeDealerStatusSuccessAction}
 */
export const changeDealerStatusSuccess = (dealer, meta = {}) => ({
  type: types.CHANGE_DEALER_STATUS_SUCCESS,
  payload: dealer,
  meta,
});

/**
 * @param {string} error
 * @returns {import('modules/dealers/types').ChangeDealerStatusFailureAction}
 */
export const changeDealerStatusFailure = error => ({
  type: types.CHANGE_DEALER_STATUS_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {string} id
 * @param {import('modules/global/typing/enums/dealers.enum').DealerStatusEnum} status
 * @returns {(dispatch:import('redux').Dispatch) => Promise<Dealer>}
 */
export const changeDealerStatus = (id, status) => dispatch => {
  dispatch(changeDealerStatusRequest(id, status));
  return new Promise((resolve, reject) => {
    DealerService.changeDealerStatus(id, status)
      .then(response => {
        dispatch(changeDealerStatusSuccess(response.data));
        resolve(response.data);
      })
      .catch(error => {
        dispatch(changeDealerStatusFailure(error));
        reject(error);
      });
  });
};

/**
 * @param {boolean} isValidating
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').DealerNameValidatingAction}
 */
export const dealerNameValidatingAction = (isValidating, meta = {}) => ({
  type: types.DEALER_NAME_VALIDATING,
  payload: isValidating,
  meta,
});

/**
 * @param {string} name
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').DealerNameCheckAction}
 */
export const dealerNameCheckRequest = (name, meta = {}) => ({
  type: types.DEALER_NAME_CHECK_REQUEST,
  payload: {
    request: {
      method: 'HEAD',
      url: DEALERS_NAME_CHECK_URL,
      params: { name },
    },
  },
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').DealerNameCheckSuccessAction}
 */
export const dealerNameCheckSuccess = (payload, meta = {}) => ({
  type: types.DEALER_NAME_CHECK_SUCCESS,
  payload,
  meta,
});

/**
 * @param {string} [error=undefined]
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').DealerNameCheckFailureAction}
 */
export const dealerNameCheckFailure = (error, meta = {}) => ({
  type: types.DEALER_NAME_CHECK_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/dealers/types').OpenModalSelectorActionPayload} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').OpenModalSelectorAction}
 */
export const openModalSelector = (payload, meta = {}) => ({
  type: actionTypes.OPEN_SELECT_DEALER_MODAL,
  payload,
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').CloseModalSelectorAction}
 */
export const closeModalSelector = (payload, meta = {}) => ({
  type: actionTypes.CLOSE_SELECT_DEALER_MODAL,
  payload,
  meta,
});

/**
 * @param {import('modules/dealers/types').RequestPayload} params
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').GetDealersModalFetchAction}
 */
export const getDealersModal = (params, meta = {}) => ({
  type: actionTypes.GET_MODAL_DEALERS,
  payload: {
    request: {
      method: 'GET',
      url: DEALERS_FILTER_URL,
      params: {
        status: 'ACTIVE',
        projection: 'page',
        fields: 'id,name,groupId,general,general.contacts,countryCode',
        ...params
      },
    },
  },
  meta,
});

/**
 * @param {import("modules/global/generic.types.js").PageContent<Dealer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').GetDealersModalSuccessAction}
 */
export const getDealersModalSuccess = (data, meta = {}) => ({
  type: actionTypes.GET_MODAL_DEALERS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').GetDealersModalFailureAction}
 */
export const getDealersModalFailure = (message, meta = {}) => ({
  type: actionTypes.GET_MODAL_DEALERS_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {{[field:string] : any}} filters
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').SetDealersModalFilterAction}
 */
export const setDealersModalFilter = (filters, meta = {}) => ({
  type: actionTypes.SET_MODAL_DEALERS_FILTER,
  payload: filters,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').ClearDealersModalFilterAction}
 */
export const clearDealersModalFilter = (meta = {}) => ({
  type: actionTypes.CLEAR_MODAL_DEALERS_FILTER,
  payload: undefined,
  meta,
});

/**
 * @param {keyof(import('modules/dealers/reducer/ui/modalSelect/filters').State)} field
 * @param {string} value
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').RemoveDealersModalFilterAction}
 */
export const removeDealersModalFilter = (field, value, meta = {}) => ({
  type: actionTypes.REMOVE_MODAL_DEALERS_FILTER,
  payload: {
    field,
    value,
  },
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').FoldDealerModalSearchAction}
 */
export const foldDealerModalSearch = (payload, meta = {}) => ({
  type: actionTypes.FOLD_EXPENDED_SEARCH,
  payload,
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').UnfoldDealerModalSearchAction}
 */
export const unfoldDealerModalSearch = (payload, meta = {}) => ({
  type: actionTypes.UNFOLD_EXPENDED_SEARCH,
  payload,
  meta,
});

/**
 * @param {Object} params
 * @returns {import('modules/dealers/types').FetchDealersByReducerAction}
 */
export const fetchDealersByReducer = params => ({
  type: types.FETCH_DEALERS_BY_REDUCER_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: DEALERS_REDUCE_ALL_URL,
      params,
    },
  },
});

/**
 * @param {string} token
 * @param {import('modules/dealers/types').GetPaginatedDealersActionPayload} params
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').GetPaginatedDealersAction}
 */
export const getPaginatedDealers = (token, params, meta = {}) => ({
  type: actionTypes.GET_PAGINATED_DEALERS_FETCH,
  payload: {
    request: {
      method: 'GET',
      url: DEALERS_FILTER_URL,
      params: {
        sort: 'name,asc',
        ...params,
      },
    },
    token,
  },
  meta,
});

/**
 * @param {string} token
 * @param {import('modules/global/generic.types.js').PageContent<Dealer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').GetPaginatedDealersSuccessAction}
 */
export const getPaginatedDealersSuccess = (token, data, meta = {}) => ({
  type: actionTypes.GET_PAGINATED_DEALERS_FETCH_SUCCESS,
  payload: {
    ...data,
    token,
  },
  meta,
});

/**
 * @param {string} token
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').GetPaginatedDealersFailureAction}
 */
export const getPaginatedDealersFailure = (token, message, meta = {}) => ({
  type: actionTypes.GET_PAGINATED_DEALERS_FETCH_FAILURE,
  payload: {
    message,
    token,
  },
  meta,
});

/**
 * @param {string} token
 * @param {{[field:string] : any}} filters
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').setPaginatedDealersFilters}
 */
export const setPaginatedDealersFilters = (token, filters, meta = {}) => ({
  type: actionTypes.SET_PAGINATED_DEALERS_FILTERS,
  payload: {
    filters,
    token,
  },
  meta,
});

/**
 * @param {string} dealerId
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').LoadDealerAction}
 */
export const loadDealer = (dealerId, meta = {}) => ({
  type: actionTypes.LOAD_DEALER,
  payload: dealerId,
  meta,
});

/**
 * @param {string} dealerId
 * @param {Object} [meta={}]
 * @returns {import('modules/dealers/types').UploadUsersDealerAction}
 */
export const uploadUsersDealer = (dealerId, meta = {}) => ({
  type: actionTypes.UPLOAD_USERS_DEALER,
  payload: {
    request: {
      method: 'GET',
      url: USERS_API_BULK_INSERT_SHEET_URL,
      params: {
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        dealerId,
      },
    },
  },
  meta,
});

export const dashboardIsReady = (payload, meta = {}) => ({
  type: actionTypes.DASHBOARD_IS_READY,
  payload,
  meta,
});

/**
 * @param {string} dealerId
 * @param {Object} [meta={}]
 * @returns {import('modules/global/generic.types.js').Action<import('modules/global/generic.types.js').GetRequest<undefined>>}
 */
export const getPredefinedMessages = (dealerId, meta = {}) => ({
  type: actionTypes.PREDEFINED_MESSAGES_FETCH,
  payload: {
    request: {
      method: 'GET',
      url: `${DEALERS_URL}/${dealerId}/predefinedMessages`,
    },
  },
  meta,
});

/**
 * @param {import('modules/predefinedMessages/types').FetchPredefinedMessagesResponse} response
 * @param {Object} [meta={}]
 * @returns {import('modules/predefinedMessages/types').FetchPredefinedMessagesSuccessAction}
 */
export const getPredefinedMessagesSuccess = (response, meta = {}) => ({
  type: actionTypes.PREDEFINED_MESSAGES_FETCH_SUCCESS,
  payload: response,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/global/generic.types.js').Action<string>}
 */
export const getPredefinedMessagesFailure = (error, meta = {}) => ({
  type: actionTypes.PREDEFINED_MESSAGES_FETCH_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * Trigger manual Keyloop scheduler refresh for a specific dealer
 * @param {string} dealerId
 * @param {Object} [meta={}]
 * @returns {import("modules/dealers/types").TriggerKeyloopRefreshAction}
 */
export const triggerKeyloopRefresh = (dealerId, meta = {}) => ({
  type: actionTypes.TRIGGER_KEYLOOP_REFRESH,
  payload: {
    dealerId,
    request: {
      method: 'POST',
      url: `${KEYLOOP_API_URL}/dealers/${dealerId}`,
    },
  },
  meta,
});
